import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { idempotencyKey } from '@/lib/idempotency'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { MarkdownEditor } from './MarkdownEditor'

type Approval = components['schemas']['ApprovalOut']
type ApprovalDetail = components['schemas']['ApprovalDetailOut']
type ApprovalField = components['schemas']['ApprovalFieldOut']
type ApprovalLine = components['schemas']['ApprovalLineOut']
type Rationale = components['schemas']['ApprovalRationaleOut']
type SiteGating = components['schemas']['GatingOut']
type ContentDetail = components['schemas']['ContentDetailOut']
type QualityCheck = components['schemas']['QualityCheckOut']

type Tab = 'queue' | 'gating'

/**
 * Approvals — S-D5's `seo#approve` and `seo#gating`, on real items (S-B27).
 *
 * **The most important screen in the product**, in the design's own words, and
 * the three things its note asks for are what the layout is built around: you can
 * see exactly what changes, you can see *why* without leaving, and you can edit
 * before accepting. So the diff, the reasoning and the editor are one view — the
 * reasoning is a panel beside the change rather than a link, because an approval
 * made without it is a rubber stamp.
 *
 * **Two tabs rather than two routes.** *What we can do without asking* is the
 * same decision as the queue, made in advance and for every future run: a tenant
 * who is tired of approving alt-text fixes reaches for it from the queue, in the
 * queue's own words. Splitting it into `/settings` would put the switch that
 * turns approvals off somewhere the person doing approvals is not.
 *
 * **The queue is organisation-wide and the gating is per site**, which is the
 * API's shape and `app/v1/seo_approvals.py` argues both halves: one morning pass
 * over everything waiting, and a separate answer to *"may we publish without
 * asking"* for a staging domain and a client's live one.
 *
 * WHAT THE BUTTON SAYS
 * ----------------------
 * S-D5 draws *"Approve & publish"*. It says **Approve**, and the card says what
 * that does, because approving moves the document to `approved` and no further
 * (`decide_one`'s own docstring). This is the same call S-B23 made about *"Fixed
 * automatically"* on the audit screen, for the same reason — a screen that claims
 * an act it did not perform is the one kind of wrong an approval queue must never
 * be.
 *
 * **S-G5 changed the sentence under the editor and not the button.** It used to
 * say pushing to your website *"is not built yet"*, which was true while S-B28's
 * five endpoints had no caller and is the audit's §V3. `/seo/publish-targets`
 * exists now, so the note links to it; the button stays *Approve*, because the
 * two acts really are separate and `POST …/publish` is still a second press.
 */
export function ApprovalQueue(): JSX.Element {
  const [tab, setTab] = useState<Tab>('queue')
  const [trail, setTrail] = useState<readonly string[]>([])

  const cursor = trail[trail.length - 1] ?? null

  const queue = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/approvals', {
          params: { query: cursor === null ? {} : { cursor } },
          signal,
        }),
      ),
    [cursor],
  )

  // Loaded only once the tab is opened. The queue is what a tenant came for and
  // the gating is a settings screen behind it; fetching both on arrival would
  // spend a round trip on the panel nobody looked at.
  const gating = useQuery(
    (signal) =>
      tab === 'gating'
        ? call(() => api.GET('/api/v1/seo/approvals/sites/gating', { signal }))
        : Promise.resolve({ ok: true as const, data: null }),
    [tab],
  )

  const waiting = queue.state.data?.waiting ?? 0

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">Approvals</h1>
        <span className="app-spacer" />
        {waiting > 0 ? (
          <span className="ar-badge ar-badge--warning">{`${String(waiting)} waiting`}</span>
        ) : null}
      </div>

      <div className="ar-tabs" role="tablist" aria-label="Approval views">
        <TabButton id="queue" label="Queue" count={waiting} active={tab} onSelect={setTab} />
        <TabButton
          id="gating"
          label="What we can do without asking"
          active={tab}
          onSelect={setTab}
        />
      </div>

      {tab === 'queue' ? (
        <QueueTab
          queue={queue.state}
          refetch={queue.refetch}
          canPageBack={trail.length > 0}
          onOlder={(next) => {
            setTrail([...trail, next])
          }}
          onNewer={() => {
            setTrail(trail.slice(0, -1))
          }}
        />
      ) : (
        <GatingTab state={gating.state} refetch={gating.refetch} />
      )}
    </>
  )
}

/* --- the queue ---------------------------------------------------------------- */

type QueueState = ReturnType<typeof useQuery<components['schemas']['ApprovalQueueOut']>>['state']

/**
 * The list, the change, and the decision.
 *
 * Its own component so that the selection, the draft and the reject dialog reset
 * when the tab is left — a half-typed rejection reason that survived a trip
 * through the gating screen and attached itself to a different article is the
 * bug this shape makes unreachable.
 */
function QueueTab({
  queue,
  refetch,
  canPageBack,
  onOlder,
  onNewer,
}: {
  queue: QueueState
  refetch: () => void
  canPageBack: boolean
  onOlder: (cursor: string) => void
  onNewer: () => void
}): JSX.Element {
  const toast = useToast()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [checked, setChecked] = useState<readonly string[]>([])
  const [rejecting, setRejecting] = useState<Approval | null>(null)
  const [reason, setReason] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)

  const items = queue.data?.items ?? []
  const next = queue.data?.next_cursor ?? null
  // The first item, until one is chosen — and back to the first when the chosen
  // one leaves the queue, which is what a decision does to it.
  const selected = items.some((item) => item.id === selectedId)
    ? selectedId
    : (items[0]?.id ?? null)

  const detail = useQuery(
    (signal) =>
      selected === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/approvals/{approval_id}', {
              params: { path: { approval_id: selected } },
              signal,
            }),
          ),
    [selected],
  )

  const decide = useMutation((input: { id: string; approve: boolean; reason: string | null }) =>
    call(() =>
      api.POST('/api/v1/seo/approvals/{approval_id}/decision', {
        params: {
          path: { approval_id: input.id },
          header: {
            'Idempotency-Key': idempotencyKey(input.approve ? 'approve' : 'reject', input.id),
          },
        },
        body: { approve: input.approve, reason: input.reason },
      }),
    ),
  )

  const bulk = useMutation((ids: readonly string[]) =>
    call(() =>
      api.POST('/api/v1/seo/approvals/decisions', {
        params: {
          // The ids are not in the key: a hundred UUIDs is past the 200
          // characters `app/idempotency.validate` allows, and the random half is
          // what makes a key unique in any case.
          header: { 'Idempotency-Key': idempotencyKey('approve-many', String(ids.length)) },
        },
        body: { approval_ids: [...ids], approve: true, reason: null },
      }),
    ),
  )

  const saveEdits = useMutation((input: Draft) =>
    call(() =>
      api.PUT('/api/v1/seo/approvals/{approval_id}/edits', {
        params: {
          path: { approval_id: input.id },
          header: { 'Idempotency-Key': idempotencyKey('edits', input.id) },
        },
        body: {
          title: input.title,
          meta_description: input.meta,
          body_markdown: input.body,
          image_alt: input.alts,
        },
      }),
    ),
  )

  const loaded = detail.state.data ?? null
  const stored = loaded === null ? null : draftOf(loaded)
  // Held against the id it was typed for, so switching items cannot carry one
  // article's words onto another's. No effect and no reset: the draft is simply
  // ignored the moment it stops matching what is on screen.
  const editing = stored === null ? null : draft !== null && draft.id === stored.id ? draft : stored
  const dirty = stored !== null && editing !== null && !sameDraft(stored, editing)

  // S-G6, audit §G13 and §V2. The gate's verdict on the article being approved,
  // and the run its reasoning belongs to. A second request per selected item
  // rather than a field on `ApprovalDetailOut`: an approval is not always about
  // a document — a `technical_fix` has no gate and no draft — and widening the
  // approval schema to carry a content-only object would put a null on every
  // row of the other kind.
  const article = useQuery(
    (signal) =>
      loaded === null || loaded.subject_type !== 'content'
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/content/{content_id}', {
              params: { path: { content_id: loaded.subject_id } },
              signal,
            }),
          ),
    [loaded?.subject_type, loaded?.subject_id],
  )

  // **The answer in hand, only while it is about the item on screen.** Moving to
  // the next approval changes `loaded` a render before this query is re-issued,
  // and rendering the previous article's gate under the new one's heading is the
  // one thing an approval screen must never do — somebody approves against a
  // verdict that belongs to a different document. Null in that render, which the
  // card below shows as loading.
  const gated =
    article.state.data !== null && article.state.data.id === loaded?.subject_id
      ? article.state.data
      : null

  function afterDecision(): void {
    setDraft(null)
    setSelectedId(null)
    setChecked([])
    refetch()
  }

  return (
    <div className="ar-tabpanel" role="tabpanel" aria-label="Queue">
      {decide.state.status === 'error' && rejecting === null ? (
        <InlineError failure={decide.state.failure} />
      ) : null}
      {bulk.state.status === 'error' ? <InlineError failure={bulk.state.failure} /> : null}

      {queue.status === 'loading' ? (
        <Loading label="Loading the approval queue" />
      ) : queue.status === 'error' ? (
        <ErrorState failure={queue.failure} onRetry={refetch} />
      ) : items.length === 0 ? (
        <Empty
          icon="✓"
          title="Nothing is waiting"
          body={
            'When AugRank writes a post, rewrites a title or proposes a technical fix, it lands ' +
            'here first. Nothing reaches your site until somebody approves it.'
          }
          action={<Link to="/seo/audit">See what AugRank has found</Link>}
        />
      ) : (
        <div className="app-approve">
          <div className="ar-stack">
            {detail.state.status === 'loading' ? (
              <Loading label="Loading the change" lines={6} />
            ) : detail.state.status === 'error' ? (
              <ErrorState failure={detail.state.failure} onRetry={detail.refetch} />
            ) : loaded === null || editing === null ? null : (
              <Change
                detail={loaded}
                draft={editing}
                dirty={dirty}
                position={items.findIndex((item) => item.id === loaded.id) + 1}
                total={items.length}
                deciding={decide.pending}
                saving={saveEdits.pending}
                saveFailure={
                  saveEdits.state.status === 'error' ? saveEdits.state.failure.message : undefined
                }
                onEdit={setDraft}
                onSave={() => {
                  void saveEdits.run(editing).then((result) => {
                    if (!result.ok) return
                    setDraft(null)
                    detail.refetch()
                    toast.show({
                      kind: 'success',
                      title: 'Your edits are saved',
                      body: 'What publishes is what you just wrote, not what AugRank drafted.',
                    })
                  })
                }}
                onApprove={() => {
                  // **Unsaved edits are saved first, and this was a real defect.**
                  // *Approve* used to decide straight away, so a tenant who
                  // rewrote a paragraph and pressed the primary button — rather
                  // than the quiet *Save my edits* beside it — approved the text
                  // AugRank had drafted, and the toast told them it was theirs.
                  // The whole exit criterion of this session is that what
                  // publishes is what they wrote, and the screen was the one
                  // place it was not true.
                  const id = loaded.id
                  const decided = (): Promise<void> =>
                    decide.run({ id, approve: true, reason: null }).then((result) => {
                      if (!result.ok) return
                      afterDecision()
                      toast.show({
                        kind: 'success',
                        title: 'Approved',
                        body: 'Recorded against your name, with the exact text you approved.',
                      })
                    })
                  // `editing` is non-null in this branch — the render above
                  // returns early otherwise — so `dirty` is the whole question.
                  if (!dirty) {
                    void decided()
                    return
                  }
                  void saveEdits.run(editing).then((saved) => {
                    if (!saved.ok) return undefined
                    setDraft(null)
                    return decided()
                  })
                }}
                onReject={() => {
                  decide.reset()
                  setReason('')
                  setRejecting(loaded)
                }}
              />
            )}
          </div>

          <div className="ar-stack">
            <Why detail={loaded} article={gated} />
            {loaded === null || loaded.subject_type !== 'content' ? null : (
              <QualityGate article={gated} failure={article.state.failure} />
            )}
            <WaitingList
              items={items}
              selected={selected}
              checked={checked}
              busy={bulk.pending}
              nextCursor={next}
              canPageBack={canPageBack}
              onSelect={(id) => {
                setSelectedId(id)
              }}
              onCheck={(id, on) => {
                setChecked(on ? [...checked, id] : checked.filter((each) => each !== id))
              }}
              onApproveSelected={() => {
                void bulk.run(checked).then((result) => {
                  if (!result.ok) return
                  afterDecision()
                  const asked = (result.data.outcomes ?? []).length
                  const missed = asked - result.data.decided
                  toast.show({
                    kind: 'success',
                    title: `Approved ${String(result.data.decided)} of ${String(asked)}`,
                    body:
                      missed === 0
                        ? 'Each one is recorded separately, so the log can still say who approved what.'
                        : `${String(missed)} had already been decided by somebody else and were left alone.`,
                  })
                })
              }}
              onOlder={onOlder}
              onNewer={onNewer}
            />
          </div>
        </div>
      )}

      {rejecting === null ? null : (
        <Modal
          title="Reject this change?"
          onClose={() => {
            setRejecting(null)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setRejecting(null)
                }}
              >
                Keep it waiting
              </Button>
              <Button
                tone="danger"
                busy={decide.pending}
                busyLabel="Rejecting"
                onClick={() => {
                  const target = rejecting
                  const said = reason.trim()
                  void decide
                    .run({ id: target.id, approve: false, reason: said === '' ? null : said })
                    .then((result) => {
                      if (!result.ok) return
                      setRejecting(null)
                      setReason('')
                      afterDecision()
                      toast.show({
                        kind: 'success',
                        title: 'Rejected',
                        body: 'Nothing was published, and your reason is on the record.',
                      })
                    })
                }}
              >
                Reject
              </Button>
            </>
          }
        >
          <InlineError failure={decide.state.failure} />
          <p style={{ marginTop: 0 }}>
            {rejecting.title ?? rejecting.change_label} will not be published, and will not come
            back on the next run unless something about the page changes.
          </p>
          <Field
            label="Why"
            help="Optional, and worth writing: it is what stops AugRank proposing the same thing again, and it is kept with the decision in your audit log."
          >
            {(aria) => (
              <textarea
                className="ar-textarea"
                {...aria}
                value={reason}
                maxLength={2000}
                placeholder="The price figures are out of date."
                onChange={(event) => {
                  setReason(event.currentTarget.value)
                }}
              />
            )}
          </Field>
        </Modal>
      )}
    </div>
  )
}

/* --- one change --------------------------------------------------------------- */

/**
 * The card: what changes, the words to change, and the two presses.
 *
 * **The diff is rendered, never recomputed.** It arrives already worked out and
 * `read_approval` says why — it was captured when the item was filed, so what a
 * tenant approves is what they were shown even if the draft has since moved. A
 * second differ in TypeScript would be the copy that disagreed.
 */
function Change({
  detail,
  draft,
  dirty,
  position,
  total,
  deciding,
  saving,
  saveFailure,
  onEdit,
  onSave,
  onApprove,
  onReject,
}: {
  detail: ApprovalDetail
  draft: Draft
  dirty: boolean
  position: number
  total: number
  deciding: boolean
  saving: boolean
  saveFailure?: string | undefined
  onEdit: (draft: Draft) => void
  onSave: () => void
  onApprove: () => void
  onReject: () => void
}): JSX.Element {
  const fields = detail.diff.fields ?? []
  const hunks = detail.diff.hunks ?? []
  const actions = detail.diff.actions ?? []
  const images = detail.images ?? []
  const editable = detail.body_markdown !== null && detail.body_markdown !== undefined

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <div>
          <h3>{detail.title ?? detail.change_label}</h3>
          <div className="ar-mono ar-subtle app-editor__note">
            {detail.slug === null || detail.slug === undefined
              ? (detail.site_domain ?? 'This organisation')
              : `/${detail.slug}`}
          </div>
        </div>
        <span className="ar-card__spacer" />
        {detail.stale ? <span className="ar-badge ar-badge--warning">Waiting a while</span> : null}
        {detail.edited ? <span className="ar-badge ar-badge--info">Edited</span> : null}
        <span className="ar-badge ar-badge--neutral">{`${String(position)} of ${String(total)}`}</span>
      </div>

      <div className="ar-card__body ar-stack">
        {detail.diff.truncated ? (
          <div className="ar-alert ar-alert--warning">
            <span className="ar-alert__icon" aria-hidden="true">
              !
            </span>
            <div className="ar-alert__body">
              <strong>This change was too large to store as a diff</strong>
              The summary below is partial. The whole text is in the editor, which is what publishes
              — read that before approving.
            </div>
          </div>
        ) : null}

        {fields.map((field) => (
          <FieldDiff key={field.field} field={field} />
        ))}

        {hunks.length === 0 ? null : (
          <div>
            <div className="ar-row app-diff-head">
              <strong className="app-editor__note">
                {detail.diff.is_new ? 'Body — a new page' : 'Body — what changes'}
              </strong>
              <span className="ar-subtle app-editor__note">
                {`+${String(detail.diff.added)} added · −${String(detail.diff.removed)} removed`}
              </span>
            </div>
            <div className="app-diff">
              {hunks.flatMap((hunk, hunkIndex) =>
                (hunk.lines ?? []).map((line, lineIndex) => (
                  <DiffRow key={`${String(hunkIndex)}-${String(lineIndex)}`} line={line} />
                )),
              )}
            </div>
          </div>
        )}

        {actions.length === 0 ? null : (
          <div>
            <strong className="app-editor__note">What would be changed</strong>
            <ul className="ar-stack ar-stack--tight" style={{ marginTop: 'var(--ar-space-3)' }}>
              {actions.map((action) => (
                <li key={`${action.code}-${action.url}`} className="ar-panel">
                  <strong className="app-editor__note">{action.label}</strong>
                  {action.url === '' ? null : (
                    <span className="ar-mono ar-subtle app-editor__block">{action.url}</span>
                  )}
                  {action.remedy === '' ? null : (
                    <span className="ar-subtle app-editor__block">{action.remedy}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {editable ? (
          <>
            <Field
              label="Page title"
              help="What Google shows as the headline. Roughly sixty characters before it is cut off."
            >
              {(aria) => (
                <input
                  className="ar-input"
                  {...aria}
                  value={draft.title}
                  maxLength={300}
                  onChange={(event) => {
                    onEdit({ ...draft, title: event.currentTarget.value })
                  }}
                />
              )}
            </Field>
            <Field
              label="Meta description"
              help="The sentence under the headline in Google. Not a ranking factor; it is what decides whether anybody clicks."
            >
              {(aria) => (
                <textarea
                  className="ar-textarea"
                  {...aria}
                  value={draft.meta}
                  maxLength={500}
                  onChange={(event) => {
                    onEdit({ ...draft, meta: event.currentTarget.value })
                  }}
                />
              )}
            </Field>
            <MarkdownEditor
              label="Body"
              value={draft.body}
              busy={saving}
              error={saveFailure}
              onChange={(body) => {
                onEdit({ ...draft, body })
              }}
            />
          </>
        ) : null}

        {images.map((image, index) => (
          <div key={image.index} className="ar-grid ar-grid--2">
            {image.url === '' ? null : (
              <img className="app-approve__img" src={image.url} alt={draft.alts[index] ?? ''} />
            )}
            <div className="ar-stack ar-stack--tight">
              <Field
                label="Alt text"
                help="Written with the image, not bolted on later — it is what the technical audit checks for and what a screen reader gets."
              >
                {(aria) => (
                  <input
                    className="ar-input"
                    {...aria}
                    value={draft.alts[index] ?? ''}
                    onChange={(event) => {
                      const alts = [...draft.alts]
                      alts[index] = event.currentTarget.value
                      onEdit({ ...draft, alts })
                    }}
                  />
                )}
              </Field>
              <span className="ar-subtle app-editor__note">
                Generated images never show recognisable people or a competitor&apos;s work, and
                never claim to be a photograph of your project.
              </span>
            </div>
          </div>
        ))}

        {/*
          S-G5. This said *"…is a separate step and is not built yet, so nothing
          reaches your visitors today"* — §V3, which the audit calls §A1 as a
          tenant experiences it. The step is built now, so the sentence has to
          change: what stays true is that it is separate, because approving and
          pushing are two acts and `POST …/publish` is the second one.
        */}
        <p className="ar-subtle app-editor__note" style={{ margin: 0 }}>
          Approving records the decision against your name and marks this ready. Pushing it to your
          website is a separate step — do it from{' '}
          <Link className="ar-link" to="/seo/publish-targets">
            Where we publish
          </Link>
          , which is also where you say which site or CMS it goes to.
        </p>
      </div>

      <div className="ar-card__footer">
        <Button tone="primary" busy={deciding} busyLabel="Approving" onClick={onApprove}>
          Approve
        </Button>
        {editable ? (
          <Button tone="ghost" disabled={!dirty} busy={saving} busyLabel="Saving" onClick={onSave}>
            Save my edits
          </Button>
        ) : null}
        <span className="ar-card__spacer" />
        <Button tone="quiet" onClick={onReject}>
          Reject
        </Button>
      </div>
    </div>
  )
}

function FieldDiff({ field }: { field: ApprovalField }): JSX.Element {
  return (
    <div>
      <div className="ar-row app-diff-head">
        <strong className="app-editor__note">{field.label}</strong>
        <span className="ar-subtle app-editor__note">
          {`${String(field.before_chars)} → ${String(field.after_chars)} characters`}
        </span>
      </div>
      <div className="app-diff">
        {field.before === '' ? null : <DiffRow line={{ op: 'del', text: field.before }} />}
        <DiffRow line={{ op: field.changed ? 'add' : 'ctx', text: field.after }} />
      </div>
    </div>
  )
}

/** One line of the diff. The gutter carries the sign, so it is never colour alone. */
function DiffRow({ line }: { line: ApprovalLine }): JSX.Element {
  const modifier =
    line.op === 'add' ? ' app-diff__row--add' : line.op === 'del' ? ' app-diff__row--del' : ''
  return (
    <div className={`app-diff__row${modifier}`}>
      <span className="app-diff__gut">
        {line.op === 'add' ? '+' : line.op === 'del' ? '−' : ' '}
      </span>
      <span className="app-diff__txt">{line.text}</span>
    </div>
  )
}

/* --- why ---------------------------------------------------------------------- */

/**
 * The reasoning, quoted rather than restated.
 *
 * Every line here is a `reasoning_logs` row as it was written, with the model
 * that wrote it — `ApprovalRationaleOut` says so, and the point is that the
 * sentence a tenant reads before approving is the sentence the run recorded, not
 * a summary this screen composed of it.
 */
function Why({
  detail,
  article,
}: {
  detail: ApprovalDetail | null
  article: ContentDetail | null
}): JSX.Element {
  const steps: Rationale[] = detail?.diff.why ?? []
  const model = [...steps].reverse().find((step) => step.model !== null && step.model !== undefined)
  const tokensIn = steps.reduce((total, step) => total + (step.input_tokens ?? 0), 0)
  const tokensOut = steps.reduce((total, step) => total + (step.output_tokens ?? 0), 0)

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Why we suggest this</h3>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight app-editor__note">
        {steps.length === 0 ? (
          <p className="ar-subtle" style={{ margin: 0 }}>
            No reasoning was recorded for this change. That is worth knowing before you approve it —
            everything AugRank writes should be able to say why.
          </p>
        ) : (
          steps.map((step) => (
            <p key={step.step_number} style={{ margin: 0 }}>
              <strong>{phaseLabel(step.phase)}.</strong> {step.summary}
            </p>
          ))
        )}
        {detail === null ? null : (
          <>
            <hr className="app-approve__rule" />
            <div className="ar-row app-diff-head">
              <span className="ar-subtle">Model</span>
              <span className="ar-mono">{model?.model ?? 'Not recorded'}</span>
            </div>
            <div className="ar-row app-diff-head">
              <span className="ar-subtle">Drafted</span>
              <span className="ar-mono">{formatDate(detail.created_at)}</span>
            </div>
            <div className="ar-row app-diff-head">
              <span className="ar-subtle">Words</span>
              <span className="ar-mono">{String(detail.word_count)}</span>
            </div>
            {/* **Tokens where S-D5 draws ₹4.20**, and it is the same call
                `ReasoningTrail` made about the prototype's per-run rupee badge:
                nothing joins a document to money. `usage_metering` is keyed by
                tenant, period and metric, and most of a pass's stages run
                in-process without a `job_id`, so any figure here would be
                composed by this screen rather than derived from the ledger —
                and a number that reconciles against nothing is worse than no
                number. The tokens and the model do reconcile, and the money
                stays on the budget-burn panel where `app/v1/spend.py` derives
                it from the ledger that holds it. */}
            {tokensIn + tokensOut === 0 ? null : (
              <div className="ar-row app-diff-head">
                <span className="ar-subtle">Tokens</span>
                <span className="ar-mono">
                  {`${tokensIn.toLocaleString('en-IN')} in · ${tokensOut.toLocaleString('en-IN')} out`}
                </span>
              </div>
            )}
            {/* §V2's *"See the full reasoning trail →"*. It carries the run so
                the viewer opens on **this** article's chain rather than on the
                newest one — a link that lands on somebody else's reasoning is
                worse than no link. Absent when the document records no run,
                which is true of anything written before S-B29 persisted them. */}
            {article?.run_id ? (
              <Link className="ar-link" to={`/seo/trail?run=${article.run_id}`}>
                See the full reasoning trail →
              </Link>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

/* --- the quality gate --------------------------------------------------------- */

/** `QualityCheckOut.code` → the mockup's label, in the order it lists them. */
const GATE_CHECKS: Record<string, string> = {
  originality: 'Originality',
  fact_check: 'Fact check',
  brand_voice: 'Brand voice',
  eeat: 'E-E-A-T',
  internal_links: 'Internal links',
}

const GATE_VERDICT: Record<string, { tone: string; mark: string }> = {
  pass: { tone: 'ar-badge--success', mark: '✓' },
  warn: { tone: 'ar-badge--warning', mark: '⚠' },
  fail: { tone: 'ar-badge--danger', mark: '✕' },
}

/**
 * What the quality gate made of this draft — audit §G13, and the missing card
 * §V2 records in the rail.
 *
 * **Everything in the queue has passed**, which is the point rather than a
 * reason to leave it out: `quality_failed` is a terminal state and a blocked
 * draft never reaches an approver at all. So this card is the evidence for a
 * decision somebody is about to make with their name on it — *four claims
 * sourced, 3% similarity, brand voice matched* — and without it the approver was
 * being asked to trust that a gate they could not see had run.
 *
 * A verdict older than S-B26 has no stored gate, and that says so rather than
 * showing five empty rows: `_gate_out` returns null for those documents by
 * design, and a screen inventing ticks for checks that never ran would be the
 * worse failure.
 */
function QualityGate({
  article,
  failure,
}: {
  article: ContentDetail | null
  failure: ApiFailure | null
}): JSX.Element {
  const checks: QualityCheck[] = article?.gate?.checks ?? []
  const order = Object.keys(GATE_CHECKS)
  const rows = [
    ...order
      .map((code) => checks.find((check) => check.code === code))
      .filter((check): check is QualityCheck => check !== undefined),
    ...checks.filter((check) => !order.includes(check.code)),
  ]

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Quality gate</h3>
        {article === null || rows.length === 0 ? null : (
          <>
            <span className="ar-card__spacer" />
            <span
              className={`ar-badge ${article.gate?.blocked === true ? 'ar-badge--danger' : 'ar-badge--success'}`}
            >
              {article.gate?.blocked === true ? '✕ Blocked' : '✓ Passed'}
            </span>
          </>
        )}
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {failure !== null ? (
          <p className="ar-subtle" style={{ margin: 0 }}>
            The quality gate could not be loaded. {failure.message}
          </p>
        ) : article === null ? (
          <Loading label="Loading the quality gate" lines={2} />
        ) : rows.length === 0 ? (
          <p className="ar-subtle" style={{ margin: 0 }}>
            No gate result was recorded for this draft. Anything written before the quality gate
            existed has none, and nothing is invented for it here.
          </p>
        ) : (
          rows.map((check) => {
            const verdict = GATE_VERDICT[check.verdict] ?? { tone: 'ar-badge--neutral', mark: '·' }
            return (
              <div className="app-gate__row" key={check.code}>
                <span className={`ar-badge ${verdict.tone}`}>{verdict.mark}</span>
                <span className="app-gate__grow">
                  <strong>{GATE_CHECKS[check.code] ?? check.code}</strong>
                  {check.reason === '' ? null : <span>{check.reason}</span>}
                </span>
                {check.headline === '' ? null : <span className="ar-mono">{check.headline}</span>}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

/* --- the list ----------------------------------------------------------------- */

function WaitingList({
  items,
  selected,
  checked,
  busy,
  nextCursor,
  canPageBack,
  onSelect,
  onCheck,
  onApproveSelected,
  onOlder,
  onNewer,
}: {
  items: Approval[]
  selected: string | null
  checked: readonly string[]
  busy: boolean
  nextCursor: string | null
  canPageBack: boolean
  onSelect: (id: string) => void
  onCheck: (id: string, on: boolean) => void
  onApproveSelected: () => void
  onOlder: (cursor: string) => void
  onNewer: () => void
}): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Waiting</h3>
        <span className="ar-card__spacer" />
        <span className="ar-badge ar-badge--neutral">{String(items.length)}</span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        <ul className="ar-stack ar-stack--tight app-approve__list">
          {items.map((item) => (
            <li key={item.id} className="ar-panel app-approve__row">
              <label className="ar-choice ar-choice--check">
                <input
                  type="checkbox"
                  checked={checked.includes(item.id)}
                  aria-label={`Select ${item.title ?? item.change_label}`}
                  onChange={(event) => {
                    onCheck(item.id, event.currentTarget.checked)
                  }}
                />
                <span className="ar-choice__box" aria-hidden="true" />
              </label>
              <button
                type="button"
                className="app-approve__pick"
                aria-current={item.id === selected ? 'true' : undefined}
                onClick={() => {
                  onSelect(item.id)
                }}
              >
                <strong className="app-editor__note">{item.title ?? item.change_label}</strong>
                <span className="ar-subtle app-editor__block">
                  {[
                    item.change_label,
                    item.site_domain ?? 'This organisation',
                    formatDate(item.created_at),
                  ].join(' · ')}
                </span>
                {item.stale ? (
                  <span className="ar-badge ar-badge--warning">Waiting a while</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
        {nextCursor === null && !canPageBack ? null : (
          <div className="ar-btn-group">
            <Button size="sm" disabled={!canPageBack} onClick={onNewer}>
              Newer
            </Button>
            <Button
              size="sm"
              disabled={nextCursor === null}
              onClick={() => {
                if (nextCursor !== null) onOlder(nextCursor)
              }}
            >
              Older
            </Button>
          </div>
        )}
      </div>
      <div className="ar-card__footer">
        <Button
          tone="primary"
          size="sm"
          disabled={checked.length === 0}
          busy={busy}
          busyLabel="Approving"
          onClick={onApproveSelected}
        >
          Approve selected
        </Button>
        <span className="ar-subtle app-editor__note">
          {checked.length === 0
            ? 'Tick the ones you have read.'
            : `${String(checked.length)} chosen`}
        </span>
      </div>
    </div>
  )
}

/* --- gating -------------------------------------------------------------------- */

type GatingState = ReturnType<
  typeof useQuery<components['schemas']['GatingListOut'] | null>
>['state']

/**
 * *What can we do without asking?* — S-D5's screen 08, per site.
 *
 * **Every toggle renders off unless the API says `true`.** That is not this
 * screen being cautious on top of a safe server; it is the same rule stated at
 * both ends, and `app/seo/gating.py` spells out why the comparison is `=== true`
 * rather than truthiness: `approval_config` is a `jsonb` column that can hold a
 * missing key, a null, or the string `"false"`, and every one of those means
 * gated. The design's mock draws the last two toggles pre-checked; its own alert
 * above them says *"Nothing below is on unless you turn it on"*, and the alert is
 * the normative half.
 */
function GatingTab({ state, refetch }: { state: GatingState; refetch: () => void }): JSX.Element {
  const sites = state.data?.sites ?? []

  return (
    <div className="ar-tabpanel" role="tabpanel" aria-label="What we can do without asking">
      {state.status === 'loading' ? (
        <Loading label="Loading your sites" />
      ) : state.status === 'error' ? (
        <ErrorState failure={state.failure} onRetry={refetch} />
      ) : sites.length === 0 ? (
        <Empty
          title="No site yet"
          body="Add your website and you can decide what AugRank may change on it without asking."
          action={<Link to="/onboarding/site">Add your site</Link>}
        />
      ) : (
        <div className="ar-stack">
          {sites.map((site) => (
            <GatingCard key={site.site_id} site={site} onSaved={refetch} />
          ))}
        </div>
      )}
    </div>
  )
}

function GatingCard({ site, onSaved }: { site: SiteGating; onSaved: () => void }): JSX.Element {
  const toast = useToast()
  const [automatic, setAutomatic] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      CHANGES.map((change) => [change.code, site.automatic[change.code] === true]),
    ),
  )
  const [role, setRole] = useState(site.approver_role)
  const [stale, setStale] = useState(site.stale_after_days)

  const save = useMutation(() =>
    call(() =>
      api.PUT('/api/v1/seo/approvals/sites/{site_id}/gating', {
        params: { path: { site_id: site.site_id } },
        body: { automatic, approver_role: role, stale_after_days: stale },
      }),
    ),
  )

  const days = STALE_CHOICES.includes(stale)
    ? STALE_CHOICES
    : [...STALE_CHOICES, stale].sort(ascending)

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>What can we do to {site.domain} without asking?</h3>
      </div>
      <div className="ar-card__body ar-stack">
        <div className="ar-alert ar-alert--info">
          <span className="ar-alert__icon" aria-hidden="true">
            ◷
          </span>
          <div className="ar-alert__body">
            <strong>Everything is approval-gated by default</strong>
            Nothing below is on unless you turn it on. You can change this at any time and it
            applies to the next run, never retroactively.
          </div>
        </div>

        {/* **The blast-radius lines below describe what each switch will do, and
            one of them says pages appear on your site.** That is true of the
            setting and not yet true of the product: nothing publishes until the
            publishers land, so this says so rather than letting the switch make a
            promise the release cannot keep. Same call as the *Approve* button,
            and S-B23's on "Fixed automatically". */}
        <div className="ar-alert ar-alert--warning">
          <span className="ar-alert__icon" aria-hidden="true">
            ⚠
          </span>
          <div className="ar-alert__body">
            <strong>Nothing reaches your site yet</strong>
            Publishing to WordPress, Webflow, Shopify and git arrives in a later release. Until then
            a change you switch on here is approved without asking you and then waits with the rest
            — so the switches decide who approves, not yet what goes live.
          </div>
        </div>

        <InlineError failure={save.state.failure} />

        {CHANGES.map((change) => (
          <div key={change.code} className="ar-panel app-approve__row app-approve__row--top">
            <label className="ar-switch">
              <input
                type="checkbox"
                checked={automatic[change.code] === true}
                aria-label={change.label}
                onChange={(event) => {
                  setAutomatic({ ...automatic, [change.code]: event.currentTarget.checked })
                  save.reset()
                }}
              />
              <span className="ar-switch__track" aria-hidden="true" />
            </label>
            <div className="app-approve__grow">
              <strong>{change.label}</strong>
              <span className="ar-subtle app-editor__block">{change.blast}</span>
            </div>
          </div>
        ))}

        <hr className="app-approve__rule" />

        <Field
          label="Who can approve"
          help="This can only narrow who may decide, never widen it — the roles that hold the permission are the ceiling. A separate Editor role is not built yet, so granting it would grant it to viewers too."
        >
          {(aria) => (
            <select
              className="ar-select"
              {...aria}
              value={role}
              onChange={(event) => {
                // **Narrowed rather than cast.** `approver_role` became a
                // two-value union when the API started refusing an unrecognised
                // role instead of silently defaulting it, and a `select` hands
                // back a plain `string`. Reading it through the option list is
                // what keeps the widening impossible on this side too: an
                // unexpected value leaves the floor where it was rather than
                // relaxing it to `admin`.
                const chosen = event.currentTarget.value
                if (chosen === 'owner' || chosen === 'admin') setRole(chosen)
                save.reset()
              }}
            >
              <option value="admin">Owner and Admin</option>
              <option value="owner">Owner only</option>
            </select>
          )}
        </Field>

        <Field
          label="If nobody approves within"
          help="We never auto-approve on a timeout. Drafts wait indefinitely if you want."
        >
          {(aria) => (
            <select
              className="ar-select"
              {...aria}
              value={String(stale)}
              onChange={(event) => {
                setStale(Number(event.currentTarget.value))
                save.reset()
              }}
            >
              {days.map((option) => (
                <option key={option} value={String(option)}>
                  {option === 0 ? 'Never remind me' : `${String(option)} days — remind me again`}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>
      <div className="ar-card__footer">
        <span className="ar-card__spacer" />
        <Button
          tone="primary"
          busy={save.pending}
          busyLabel="Saving"
          onClick={() => {
            void save.run(undefined).then((result) => {
              if (!result.ok) return
              // Re-read rather than trust the press: the answer is what was
              // stored, and an unrecognised value is dropped by the server. A
              // settings screen that showed the request instead of the row is
              // how a tenant comes to believe automation is off when it is on.
              onSaved()
              toast.show({
                kind: 'success',
                title: `Saved for ${site.domain}`,
                body: 'It applies to the next run. Anything already waiting stays waiting.',
              })
            })
          }}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

/* --- plumbing ------------------------------------------------------------------ */

interface Draft {
  id: string
  title: string
  meta: string
  body: string
  alts: string[]
}

function draftOf(detail: ApprovalDetail): Draft {
  return {
    id: detail.id,
    title: detail.title ?? '',
    meta: detail.meta_description,
    body: detail.body_markdown ?? '',
    alts: (detail.images ?? []).map((image) => image.alt),
  }
}

function sameDraft(a: Draft, b: Draft): boolean {
  return (
    a.title === b.title &&
    a.meta === b.meta &&
    a.body === b.body &&
    a.alts.length === b.alts.length &&
    a.alts.every((alt, index) => alt === b.alts[index])
  )
}

function ascending(a: number, b: number): number {
  return a - b
}

/**
 * The reminder windows S-D5 offers, plus *never*.
 *
 * Whatever the site actually holds is added to the list if it is not one of
 * these, because `stale_after_days` accepts anything up to 90 — and a select that
 * silently showed "7 days" for a site set to 30 would be a settings screen
 * reporting a value it is about to overwrite.
 */
const STALE_CHOICES: readonly number[] = [0, 7, 14]

/**
 * The four toggles, with the blast radius the design states beside each one.
 *
 * The codes are `app/seo/gating.CHANGES` and the order is its order — worst
 * blast radius first, which the module's own comment argues: a list that
 * reordered them would put *"we will publish pages you have not read"* under
 * *"we will fix your alt text"*.
 */
const CHANGES: readonly { code: string; label: string; blast: string }[] = [
  {
    code: 'new_content',
    label: 'Publish new blog posts automatically',
    blast: 'Highest blast radius. New pages appear on your site without you seeing them first.',
  },
  {
    code: 'meta',
    label: 'Change titles and meta descriptions',
    blast: 'Affects how you appear in Google. Reversible, but visible to the public immediately.',
  },
  {
    code: 'internal_links',
    label: 'Add internal links between existing pages',
    blast: 'Low risk. Does not change your words, only adds links between your own pages.',
  },
  {
    code: 'technical',
    label: 'Fix alt text, schema, and sitemaps',
    blast: 'Invisible to visitors. Purely technical corrections.',
  },
]

/** `reasoning_logs.phase`, in the tenant's language. Unknown phases pass through. */
function phaseLabel(phase: string): string {
  return PHASES[phase] ?? phase
}

const PHASES: Record<string, string> = {
  audit: 'What we looked at',
  decision: 'What we decided',
  action: 'What we did',
  research: 'What we found',
  outline: 'How we planned it',
  draft: 'What we wrote',
  review: 'What we checked',
}

/**
 * A tab. The same shape `Keywords.tsx` uses, restated rather than shared: two
 * screens with two `Tab` unions do not have one component between them, and
 * lifting it would mean a generic over the union for four lines of markup.
 */
function TabButton({
  id,
  label,
  count,
  active,
  onSelect,
}: {
  id: Tab
  label: string
  count?: number | undefined
  active: Tab
  onSelect: (tab: Tab) => void
}): JSX.Element {
  return (
    <button
      type="button"
      className="ar-tab"
      role="tab"
      aria-selected={active === id}
      onClick={() => {
        onSelect(id)
      }}
    >
      {label}
      {count === undefined ? null : <span className="ar-tab__count">{String(count)}</span>}
    </button>
  )
}
